"use client";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useBoard } from "@/components/BoardProvider";

type Theme = "official" | "tmua-fyi";

interface ThemeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onThemeSelected: (theme: Theme) => void;
  examSubject: string;
}

/** A thumbnail of each session layout, drawn in the theme's own palette. */
function Preview({ theme }: { theme: Theme }) {
  if (theme === "official") {
    // The official view is a fixed Pearson VUE palette (see OfficialThemeSessionView).
    return (
      <div className="flex aspect-[4/3] w-full flex-col overflow-hidden rounded-lg bg-[#EFEFEF]">
        <div className="flex h-[18%] items-center bg-[#005EAA] px-2">
          <div className="h-1 w-1/3 rounded-full bg-white/70" />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 bg-white p-2.5">
          <div className="h-1 w-5/6 rounded-full bg-black/30" />
          <div className="h-1 w-3/5 rounded-full bg-black/20" />
          <div className="mt-auto flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <div className="size-1.5 rounded-full border border-black/40" />
              <div className="h-1 w-1/3 rounded-full bg-black/20" />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="size-1.5 rounded-full border border-black/40 bg-black/40" />
              <div className="h-1 w-1/2 rounded-full bg-black/20" />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="size-1.5 rounded-full border border-black/40" />
              <div className="h-1 w-2/5 rounded-full bg-black/20" />
            </div>
          </div>
        </div>
        <div className="flex h-[16%] items-center justify-end gap-1 bg-[#EFEFEF] px-2">
          <div className="h-1.5 w-4 rounded-sm bg-black/25" />
          <div className="h-1.5 w-4 rounded-sm bg-[#005EAA]" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex aspect-[4/3] w-full overflow-hidden rounded-lg bg-sidebar">
      <div className="flex w-[22%] flex-col gap-1.5 p-2">
        <div className="size-2 rounded-full bg-primary" />
        <div className="mt-1 h-1 w-full rounded-full bg-foreground/15" />
        <div className="h-1 w-3/4 rounded-full bg-foreground/15" />
        <div className="h-1 w-full rounded-full bg-foreground/15" />
      </div>
      <div className="flex flex-1 flex-col gap-1.5 rounded-tl-lg border-l border-t border-border bg-background p-2.5">
        <div className="h-1 w-5/6 rounded-full bg-foreground/30" />
        <div className="h-1 w-3/5 rounded-full bg-foreground/20" />
        <div className="mt-auto flex flex-col gap-1">
          <div className="h-2 w-full rounded-sm border border-border" />
          <div className="h-2 w-full rounded-sm border border-primary bg-primary/15" />
          <div className="h-2 w-full rounded-sm border border-border" />
        </div>
      </div>
    </div>
  );
}

export default function ThemeSelectionModal({
  isOpen,
  onClose,
  onThemeSelected,
  examSubject,
}: ThemeSelectionModalProps) {
  const board = useBoard();
  const options: {
    id: Theme;
    title: string;
    description: string;
    recommended?: boolean;
  }[] = [
    {
      id: "tmua-fyi",
      title: board.domains[0] ?? "Modern",
      description: "Clean, quick to read, built for practice.",
      recommended: true,
    },
    {
      id: "official",
      title: `Official ${examSubject}`,
      description: "The screen you sit the real test on.",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="gap-0 p-0 sm:max-w-md">
        <div className="px-6 pt-6">
          <DialogTitle className="text-lg font-semibold tracking-tight text-foreground">
            Choose a look
          </DialogTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            You can switch at any point during the sit.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 p-6">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onThemeSelected(o.id)}
              className="group flex flex-col gap-3 rounded-xl text-left transition-transform duration-150 ease-out active:scale-[0.98]"
            >
              <div className="rounded-xl p-1 ring-1 ring-border transition-[box-shadow] duration-150 group-hover:ring-2 group-hover:ring-primary group-focus-visible:ring-2 group-focus-visible:ring-primary">
                <Preview theme={o.id} />
              </div>
              <div className="px-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {o.title}
                  </span>
                  {o.recommended && (
                    <span className="text-[11px] font-medium text-primary">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {o.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
